import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retailsync/ui'
import { Button } from '@retailsync/ui'
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { API_ORIGIN, getAuthHeaders, readApiResponse } from '../config/api'

const API_BASE = API_ORIGIN

async function fetchFromApi<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}/api/v1/connector${path}`, { headers: getAuthHeaders() })
  const json = await readApiResponse<{ success: boolean; data: T; error?: string }>(res)
  if (!json.success) throw new Error(json.error || 'Unknown error')
  return json.data
}

interface DashboardStats {
  totalImports: number
  successfulImports: number
  failedImports: number
  pendingImports: number
}

interface RecentImport {
  id: string
  name: string
  status: string
  records: number
  date: string
  entity: string
}

/**
 * What the connector can ingest TODAY.
 *
 * This list used to read "Square POS / Shopify / QuickBooks / JSON APIs" with a
 * green check on each, which read as four live integrations. They are not live:
 * vendor adapter code exists under packages/connector-adapters, but no code path
 * calls it — the connector runtime only parses files. Listing them as verified
 * capabilities was a claim the product could not honour.
 *
 * These entries are the paths that actually work end to end.
 */
const supportedSystems = [
  'CSV / Excel exports',
  'RetailsyncGo POS (native)',
  'Any POS that exports files',
  'Field mapping templates',
]

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentImports, setRecentImports] = useState<RecentImport[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    setLoading(true)
    setError(null)
    try {
      const [summary, recent] = await Promise.all([
        fetchFromApi<DashboardStats>('/analytics/summary').catch(() => null),
        fetchFromApi<RecentImport[]>('/analytics/recent').catch(() => []),
      ])
      setStats(summary)
      setRecentImports(Array.isArray(recent) ? recent : [])
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  // ── Loading skeleton ──
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-200 animate-pulse rounded-lg h-32" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-100 animate-pulse rounded-lg h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-100 animate-pulse rounded-lg h-64" />
          <div className="bg-gray-100 animate-pulse rounded-lg h-64" />
        </div>
      </div>
    )
  }

  // ── Fallback numbers ──
  const s = stats || { totalImports: 0, successfulImports: 0, failedImports: 0, pendingImports: 0 }
  const successRate = s.totalImports > 0 ? ((s.successfulImports / s.totalImports) * 100).toFixed(1) : '0.0'

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Welcome to Universal Connector</h2>
            <p className="text-primary-100 mb-4">
              Import products, customers, inventory and sales from any POS that can export
              a file — CSV or Excel, any column layout, auto-detected.
            </p>
            <Link to="/import">
              <Button className="bg-white text-primary-600 hover:bg-gray-50">
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                Start New Import
              </Button>
            </Link>
          </div>
          <LinkIcon className="h-16 w-16 text-primary-200 hidden sm:block" />
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
          <Button onClick={loadDashboard} variant="ghost" size="sm" className="ml-auto !text-red-700">
            Retry
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Imports</CardTitle>
            <DocumentTextIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{s.totalImports}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{s.successfulImports}</div>
            <p className="text-xs text-muted-foreground">{successRate}% success rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{s.failedImports}</div>
            <p className="text-xs text-muted-foreground">Needs attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <ClockIcon className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{s.pendingImports}</div>
            <p className="text-xs text-muted-foreground">In progress</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Imports */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Imports</CardTitle>
            <CardDescription>Your latest data import activities</CardDescription>
          </CardHeader>
          <CardContent>
            {recentImports.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No recent imports. Start by connecting a provider.</p>
            ) : (
              <div className="space-y-4">
                {recentImports.map((imp) => (
                  <div key={imp.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{imp.name}</p>
                      <p className="text-xs text-gray-500">
                        {imp.records} records • {imp.date} • {imp.entity}
                      </p>
                    </div>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                      imp.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : imp.status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {imp.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4">
              <Link to="/history">
                <Button variant="outline" className="w-full">
                  View All Imports
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Supported Systems */}
        <Card>
          <CardHeader>
            <CardTitle>What you can import</CardTitle>
            <CardDescription>Formats supported today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {supportedSystems.map((system) => (
                <div key={system} className="flex items-center p-3 border rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium">{system}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Link to="/import">
                <Button className="w-full">
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  Import from Any System
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common import tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/import">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center">
                <ArrowUpTrayIcon className="h-6 w-6 mb-2" />
                <span className="text-sm font-medium">New Import</span>
                <span className="text-xs text-gray-500">Upload data file</span>
              </Button>
            </Link>

            <Link to="/mapping">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center">
                <LinkIcon className="h-6 w-6 mb-2" />
                <span className="text-sm font-medium">Data Mapping</span>
                <span className="text-xs text-gray-500">Configure field mappings</span>
              </Button>
            </Link>

            <Link to="/history">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center">
                <ClockIcon className="h-6 w-6 mb-2" />
                <span className="text-sm font-medium">Import History</span>
                <span className="text-xs text-gray-500">View past imports</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}